import { type Checkout } from "@client/content/modules/Checkout/types";
import { type StripeCustomerInfo } from "@common/components/Form/Field/StripeCard/lib";
import FormField, { type FormFieldProps } from "@common/controls/Form/Field/component";
import { type InputTypeProps } from "@common/declarations";
import { convertToAmountInCents } from "@common/lib/currency";
import { joinDefined } from "@common/lib/misc";
import { Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, type StripeElementsOptions } from "@stripe/stripe-js";
import React from "react";
import StripePaymentInput from "./Input";

export type CreateStripeIntentResponse = {
  isSetup: boolean;
  secret: string;
};
export type CreateStripeIntent = () => Promise<CreateStripeIntentResponse>;

type PropsBase = Omit<
  FormFieldProps<Checkout.View.StripePaymentInfo | undefined, InputTypeProps<string>>,
  "Component" | "componentOpts" | "validator"
> & {
  createIntent: CreateStripeIntent;
  customer?: StripeCustomerInfo;
  inputStyle?: React.CSSProperties;
};
const StripePaymentInputFormFieldBase = ({ createIntent, customer, inputStyle, ...fieldProps }: PropsBase) => {
  const stripe = useStripe();
  const elements = useElements();

  const [paymentInfo, setPaymentInfo] = React.useState<Checkout.View.StripePaymentInfo | null>(null);

  const getFinalValue = React.useCallback(async () => {
    if (paymentInfo) return paymentInfo;

    if (!stripe || !elements) return null;

    const { error: submitError, selectedPaymentMethod } = await elements.submit();
    if (submitError) throw new Error(submitError.message);

    const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
      elements: elements,
      params: {
        billing_details: customer
          ? {
              email: customer.email,
              name: joinDefined(" ", customer.firstName, customer.lastName)
            }
          : undefined
      }
    });
    if (paymentMethodError) throw new Error(paymentMethodError.message);

    if (selectedPaymentMethod === "card") {
      const intentResponse = await createIntent();

      const res = await stripe.confirmPayment({
        confirmParams: {
          return_url: window.location.href,
          payment_method: paymentMethod.id
        },
        elements,
        clientSecret: intentResponse.secret,
        redirect: "if_required"
      });

      if (res.error) throw new Error(res.error.message);

      const result = { paymentIntentId: res.paymentIntent.id };
      setPaymentInfo(result);
      return result;
    }

    const result = { paymentMethodId: paymentMethod.id };
    setPaymentInfo(result);
    return result;
  }, [stripe, elements, customer, createIntent, fieldProps.value]);

  return (
    <FormField
      {...fieldProps}
      Component={StripePaymentInput}
      getFinalValue={getFinalValue}
      isNoStyle
      componentProps={{ style: inputStyle }}
      value={{ paymentIntentId: "dummy" }}
    />
  );
};

export type StripeCardInputFormFieldProps = PropsBase & {
  fontCssSrc?: string;
  publishableKey: string;
  amount: number;
  currencyCode: string;
  hasSubscriptionItems: boolean;
};
const StripePaymentInputFormField = ({ publishableKey, fontCssSrc, ...props }: StripeCardInputFormFieldProps) => {
  const stripePromise = loadStripe(publishableKey);

  const options = React.useMemo(
    (): StripeElementsOptions | undefined => ({
      currency: props.currencyCode.toLowerCase(),
      mode: props.amount === 0 ? "setup" : "payment",
      amount: convertToAmountInCents(props.amount, props.currencyCode),
      setup_future_usage: props.hasSubscriptionItems ? "off_session" : undefined,
      paymentMethodCreation: "manual",
      fonts: fontCssSrc
        ? [
            {
              cssSrc: fontCssSrc
            }
          ]
        : undefined
    }),
    [fontCssSrc]
  );

  return (
    <Elements options={options} stripe={stripePromise}>
      <StripePaymentInputFormFieldBase {...props} />
    </Elements>
  );
};
export default StripePaymentInputFormField;
